import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { SERVICES } from "../config";

const linkCls =
  "relative text-sm font-semibold tracking-wide text-[#22444a] transition-colors duration-300 hover:text-[#14717A] after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-[#4CA6AF] after:transition-[width] after:duration-300 hover:after:w-full cursor-pointer";

const OpenIndicator = ({ className = "" }) => {
  const [isOpen, setIsOpen] = useState(null);
  useEffect(() => {
    const check = () => {
      const h = new Date().getHours();
      setIsOpen(h >= 10 && h < 21);
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, []);
  if (isOpen === null) return null;
  return (
    <div
      data-testid="clinic-status-indicator"
      className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold ${
        isOpen ? "bg-[#e7f6ef] text-[#14717A]" : "bg-[#eef1f3] text-[#8A9CAB]"
      } ${className}`}
    >
      <span className="relative flex h-2 w-2">
        {isOpen && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-60" />
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${isOpen ? "bg-[#25D366]" : "bg-[#8A9CAB]"}`} />
      </span>
      {isOpen ? "Clinic Open Now" : "Opens 10:00 AM"}
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [mobileSvc, setMobileSvc] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileSvc(false);
    setSvcOpen(false);
  }, [location]);

  const goSection = (hash) => {
    setOpen(false);
    const scrollToEl = () => {
      const el = document.querySelector(hash);
      if (el) {
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
        else el.scrollIntoView({ behavior: "smooth" });
      }
    };
    if (location.pathname === "/") {
      scrollToEl();
      setTimeout(scrollToEl, 900);
    } else {
      navigate("/" + hash);
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl transition-shadow duration-500 ${
        scrolled ? "shadow-[0_8px_30px_rgba(20,113,122,0.10)]" : ""
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <div className="leading-tight">
            <p className="whitespace-nowrap font-heading text-lg font-semibold text-[#14717A] sm:text-2xl">
              Dr. Vishwa's
            </p>
            <p className="whitespace-nowrap text-[10px] font-bold tracking-[0.22em] uppercase text-[#8A9CAB] sm:text-sm sm:tracking-[0.28em]">
              Dental Hospital
            </p>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          <Link to="/" data-testid="nav-home" className={linkCls}>
            Home
          </Link>
          <button
            data-testid="nav-about"
            className={linkCls}
            onClick={() => goSection("#about-us")}
          >
            About Us
          </button>
          <div
            className="relative"
            onMouseEnter={() => setSvcOpen(true)}
            onMouseLeave={() => setSvcOpen(false)}
          >
            <Link
              to="/services"
              data-testid="nav-services"
              className={`${linkCls} flex items-center gap-1`}
            >
              Services
              <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${svcOpen ? "rotate-180" : ""}`} />
            </Link>
            {svcOpen && (
              <div
                data-testid="nav-services-dropdown"
                className="absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-3"
              >
                <div className="max-h-[70vh] overflow-y-auto rounded-2xl border border-[#DCE4E8] bg-white p-2 shadow-xl">
                  <Link
                    to="/services"
                    data-testid="nav-all-services"
                    onClick={() => setSvcOpen(false)}
                    className="block rounded-xl px-4 py-2.5 text-sm font-bold text-[#14717A] transition-colors duration-200 hover:bg-[#f2f6f7]"
                  >
                    View All Services
                  </Link>
                  <div className="mx-2 my-1 h-px bg-[#DCE4E8]" />
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services#service-${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      onClick={() => setSvcOpen(false)}
                      className="block rounded-xl px-4 py-2.5 text-sm font-medium text-[#22444a] transition-colors duration-200 hover:bg-[#f2f6f7] hover:text-[#14717A]"
                    >
                      {s.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link
            to="/stories"
            data-testid="nav-stories"
            className={linkCls}
          >
            Patient Stories
          </Link>
          <button
            data-testid="nav-reviews"
            className={linkCls}
            onClick={() => goSection("#reviews")}
          >
            Patient Reviews
          </button>
          <button
            data-testid="nav-contact"
            className={linkCls}
            onClick={() => goSection("#contact")}
          >
            Contact Us
          </button>
        </nav>

        <div className="flex items-center gap-3">
          <OpenIndicator className="hidden md:flex" />
          <Link
            to="/book"
            data-testid="nav-book-appointment"
            className="hidden rounded-full bg-[#4CA6AF] px-6 py-2.5 text-sm font-bold text-white shadow-[0_8px_20px_rgba(76,166,175,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#14717A] sm:inline-block"
          >
            Book Appointment
          </Link>
          <OpenIndicator className="md:hidden" />
          <button
            data-testid="nav-hamburger"
            aria-label="Open menu"
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#DCE4E8] text-[#14717A] lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div
        data-testid="mobile-menu"
        className={`overflow-hidden border-t border-[#DCE4E8] bg-white transition-[max-height] duration-500 lg:hidden ${
          open ? "max-h-[80vh] overflow-y-auto" : "max-h-0"
        }`}
      >
        <div className="space-y-1 px-6 py-5">
          <OpenIndicator className="mb-3 w-fit" />
          <Link to="/" data-testid="mobile-nav-home" className="block rounded-xl px-3 py-3 text-base font-semibold text-[#22444a] hover:bg-[#f2f6f7]">
            Home
          </Link>
          <button data-testid="mobile-nav-about" onClick={() => goSection("#about-us")} className="block w-full rounded-xl px-3 py-3 text-left text-base font-semibold text-[#22444a] hover:bg-[#f2f6f7]">
            About Us
          </button>
          <button
            data-testid="mobile-nav-services-toggle"
            onClick={() => setMobileSvc(!mobileSvc)}
            className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-base font-semibold text-[#22444a] hover:bg-[#f2f6f7]"
          >
            Services
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${mobileSvc ? "rotate-180" : ""}`} />
          </button>
          <div className={`overflow-hidden transition-[max-height] duration-400 ${mobileSvc ? "max-h-[50vh] overflow-y-auto" : "max-h-0"}`}>
            <Link to="/services" data-testid="mobile-nav-all-services" className="block rounded-xl px-6 py-2.5 text-sm font-bold text-[#14717A]">
              View All Services
            </Link>
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services#service-${s.slug}`}
                data-testid={`mobile-nav-service-${s.slug}`}
                className="block rounded-xl px-6 py-2.5 text-sm font-medium text-[#22444a] hover:bg-[#f2f6f7]"
              >
                {s.title}
              </Link>
            ))}
          </div>
          <Link to="/stories" data-testid="mobile-nav-stories" className="block rounded-xl px-3 py-3 text-base font-semibold text-[#22444a] hover:bg-[#f2f6f7]">
            Patient Stories
          </Link>
          <button data-testid="mobile-nav-reviews" onClick={() => goSection("#reviews")} className="block w-full rounded-xl px-3 py-3 text-left text-base font-semibold text-[#22444a] hover:bg-[#f2f6f7]">
            Patient Reviews
          </button>
          <button data-testid="mobile-nav-contact" onClick={() => goSection("#contact")} className="block w-full rounded-xl px-3 py-3 text-left text-base font-semibold text-[#22444a] hover:bg-[#f2f6f7]">
            Contact Us
          </button>
          <Link
            to="/book"
            data-testid="mobile-nav-book"
            className="mt-3 block rounded-full bg-[#4CA6AF] px-6 py-3.5 text-center text-sm font-bold text-white transition-colors duration-300 hover:bg-[#14717A]"
          >
            Book Appointment
          </Link>
        </div>
      </div>
    </header>
  );
};
